import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'

import styled from 'styled-components'

import { FormikInputField } from 'components/Form/FormikInputField/FormikInputField'
import colors from 'styles/colors'

const loadGoogleMapsScript = async (apiKey?: string) =>
  await new Promise<void>((resolve, reject) => {
    if (!apiKey) {
      reject(new Error('Missing Google Maps API key'))
      return
    }

    if (
      typeof window?.google?.maps !== 'undefined' &&
      typeof window.google.maps.importLibrary === 'function'
    ) {
      resolve()
      return
    }

    const existing = document.querySelector(
      'script[data-google-maps-script="true"]'
    )

    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true })
      existing.addEventListener(
        'error',
        () => reject(new Error('Google Maps script failed to load')),
        { once: true }
      )
      return
    }

    const script = document.createElement('script')
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&loading=async`
    script.async = true
    script.defer = true
    script.setAttribute('data-google-maps-script', 'true')
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Google Maps script failed to load'))

    document.head.appendChild(script)
  })

const isMapsReady = () =>
  typeof window?.google?.maps !== 'undefined' &&
  typeof window.google.maps.importLibrary === 'function'

type ParsedPlace = {
  name: string
  street: string
  city: string
  stateLong: string
  zip: string
  countryLong: string
}

type AddressSuggestion = {
  id: string
  mainText: string
  secondaryText: string
  fullText: string
  prediction: any
}

type GoogleAddressAutocompleteFieldProps = {
  name: string
  label: string
  value: string
  onValueChange: (value: string) => void
  onPlaceSelected: (parsed: ParsedPlace) => void
  customClass?: string
}

export type GoogleAddressAutocompleteFieldRef = {
  blur: () => void
}

const getAddressPart = (components: any[] = [], type: string) =>
  components.find(
    component => Array.isArray(component.types) && component.types.includes(type)
  )

const getPlaceName = (place: any) =>
  place?.displayName?.text ??
  place?.displayName ??
  place?.name ??
  ''

const getLongName = (components: any[] = [], type: string) =>
  getAddressPart(components, type)?.long_name ??
  getAddressPart(components, type)?.longText ??
  ''

const getPlaceComponents = (place: any): any[] => {
  if (Array.isArray(place?.address_components)) return place.address_components
  if (Array.isArray(place?.addressComponents)) return place.addressComponents
  return []
}

const parsePlace = (place: any): ParsedPlace => {
  const components = getPlaceComponents(place)
  const streetNumber = getLongName(components, 'street_number')
  const route = getLongName(components, 'route')

  return {
    name: getPlaceName(place),
    street: [streetNumber, route].filter(Boolean).join(' ').trim(),
    city:
      getLongName(components, 'locality') ||
      getLongName(components, 'postal_town') ||
      getLongName(components, 'sublocality'),
    stateLong: getLongName(components, 'administrative_area_level_1'),
    zip: getLongName(components, 'postal_code'),
    countryLong: getLongName(components, 'country'),
  }
}

const getPredictionText = (value: any) => value?.text ?? ''

export const GoogleAddressAutocompleteField = forwardRef<
  GoogleAddressAutocompleteFieldRef,
  GoogleAddressAutocompleteFieldProps
>(({ name, label, value, onValueChange, onPlaceSelected, customClass }, ref) => {
  const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY

  const [isMapsScriptReady, setIsMapsScriptReady] = useState(false)
  const [addressQuery, setAddressQuery] = useState(value || '')
  const [addressSuggestions, setAddressSuggestions] = useState<AddressSuggestion[]>([])
  const [isAddressLoading, setIsAddressLoading] = useState(false)
  const [showSuggestions, setShowSuggestions] = useState(false)

  const addressInputRef = useRef<HTMLInputElement | null>(null)
  const sessionTokenRef = useRef<any>(null)
  const requestSeqRef = useRef(0)
  const autocompleteRef = useRef<HTMLDivElement | null>(null)
  const skipNextAddressFetchRef = useRef(false)
  const hasUserTypedRef = useRef(false)

  useImperativeHandle(ref, () => ({
    blur: () => {
      addressInputRef.current?.blur()
    },
  }))

  const fetchAddressSuggestions = async (input: string) => {
    const trimmed = input.trim()

    if (!trimmed || !isMapsReady()) {
      setAddressSuggestions([])
      setShowSuggestions(false)
      setIsAddressLoading(false)
      return
    }

    const requestId = ++requestSeqRef.current
    setIsAddressLoading(true)

    try {
      const placesLib = (await google.maps.importLibrary('places')) as any

      if (!sessionTokenRef.current) {
        sessionTokenRef.current = new placesLib.AutocompleteSessionToken()
      }

      const request: any = {
        input: trimmed,
        sessionToken: sessionTokenRef.current,
        language: 'en',
      }

      const response =
        await placesLib.AutocompleteSuggestion.fetchAutocompleteSuggestions(request)

      if (requestId !== requestSeqRef.current) return

      const mapped: AddressSuggestion[] = (response?.suggestions ?? [])
        .map((suggestion: any, index: number) => {
          const prediction = suggestion?.placePrediction
          if (!prediction) return null

          return {
            id: `${prediction.placeId ?? 'prediction'}-${index}`,
            mainText: getPredictionText(prediction.mainText),
            secondaryText: getPredictionText(prediction.secondaryText),
            fullText: getPredictionText(prediction.text),
            prediction,
          }
        })
        .filter(Boolean) as AddressSuggestion[]

      setAddressSuggestions(mapped)
      setShowSuggestions(mapped.length > 0)
    } catch (error) {
      console.error('[AddressAutocomplete] failed to fetch suggestions', error)
      setAddressSuggestions([])
      setShowSuggestions(false)
    } finally {
      if (requestId === requestSeqRef.current) {
        setIsAddressLoading(false)
      }
    }
  }

const handleAddressSuggestionSelect = async (suggestion: AddressSuggestion) => {
  try {
    const place = suggestion.prediction.toPlace()

    await place.fetchFields({
      fields: ['displayName', 'formattedAddress', 'addressComponents'],
    })

    const parsed = parsePlace(place)

    skipNextAddressFetchRef.current = true
    hasUserTypedRef.current = false

    const nextStreet = parsed.street || suggestion.mainText || suggestion.fullText

    setAddressQuery(nextStreet)
    onValueChange(nextStreet)
    onPlaceSelected(parsed)

    setAddressSuggestions([])
    setShowSuggestions(false)
    sessionTokenRef.current = null

    addressInputRef.current?.blur()
  } catch (error) {
    console.error('[AddressAutocomplete] failed to fetch place details', error)
  }
}

  useEffect(() => {
    let mounted = true

    loadGoogleMapsScript(GOOGLE_MAPS_API_KEY)
      .then(() => {
        if (mounted) {
          setIsMapsScriptReady(true)
        }
      })
      .catch(error => {
        console.error('[AddressAutocomplete] Google Maps script load failed', error)
        if (mounted) {
          setIsMapsScriptReady(false)
        }
      })

    return () => {
      mounted = false
    }
  }, [GOOGLE_MAPS_API_KEY])

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null
      if (!autocompleteRef.current?.contains(target)) {
        setShowSuggestions(false)
      }
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('touchstart', handlePointerDown)

    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('touchstart', handlePointerDown)
    }
  }, [])

useEffect(() => {
  if (addressQuery !== value) {
    hasUserTypedRef.current = false
    setAddressQuery(value || '')
    setAddressSuggestions([])
    setShowSuggestions(false)
  }
}, [value])

useEffect(() => {
  if (!isMapsScriptReady || !addressQuery.trim()) {
    setAddressSuggestions([])
    setShowSuggestions(false)
    setIsAddressLoading(false)
    return
  }

  if (!hasUserTypedRef.current) {
    setAddressSuggestions([])
    setShowSuggestions(false)
    setIsAddressLoading(false)
    return
  }

  if (skipNextAddressFetchRef.current) {
    skipNextAddressFetchRef.current = false
    return
  }

  const timeoutId = setTimeout(() => {
    fetchAddressSuggestions(addressQuery)
  }, 220)

  return () => clearTimeout(timeoutId)
}, [addressQuery, isMapsScriptReady])

  return (
    <AutocompleteField ref={autocompleteRef}>
      <FormikInputField
        type='text'
        label={label}
        name={name}
        value={addressQuery}
        customClass={customClass}
        externalInputRef={addressInputRef}
        inputProps={{
          autoComplete: 'off',
          onFocus: () => {
            if (addressSuggestions.length > 0) {
              setShowSuggestions(true)
            }
          },
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
            const nextValue = e.target.value
            hasUserTypedRef.current = true
            skipNextAddressFetchRef.current = false
            setAddressQuery(nextValue)
            onValueChange(nextValue)
            },
        }}
      />

      {showSuggestions && (addressSuggestions.length > 0 || isAddressLoading) && (
        <SuggestionsDropdown>
          {isAddressLoading && addressSuggestions.length === 0 ? (
            <SuggestionLoading>Searching...</SuggestionLoading>
          ) : (
            addressSuggestions.map(item => (
              <SuggestionItem
                key={item.id}
                type='button'
                onMouseDown={e => {
                  e.preventDefault()
                  handleAddressSuggestionSelect(item)
                }}
              >
                <SuggestionMain>{item.mainText || item.fullText}</SuggestionMain>
                {!!item.secondaryText && (
                  <SuggestionSecondary>{item.secondaryText}</SuggestionSecondary>
                )}
              </SuggestionItem>
            ))
          )}
        </SuggestionsDropdown>
      )}
    </AutocompleteField>
  )
})

GoogleAddressAutocompleteField.displayName = 'GoogleAddressAutocompleteField'

const AutocompleteField = styled.div`
  position: relative;
  width: 100%;
`

const SuggestionsDropdown = styled.div`
  position: absolute;
  top: calc(100% + 0.6rem);
  left: 0;
  right: 0;
  background: ${colors.white};
  border: 0.1rem solid #d9dde5;
  border-radius: 1.6rem;
  box-shadow: 0 1rem 2rem rgba(0, 0, 0, 0.08);
  max-height: 28rem;
  overflow-y: auto;
  z-index: 20;
`

const SuggestionItem = styled.button`
  width: 100%;
  display: block;
  text-align: left;
  background: transparent;
  border: none;
  padding: 1.2rem 1.4rem;
  cursor: pointer;

  &:hover {
    background: #f6f8fb;
  }
`

const SuggestionMain = styled.div`
  font-size: 1.4rem;
  font-weight: 600;
  color: ${colors.blueDark};
`

const SuggestionSecondary = styled.div`
  font-size: 1.25rem;
  color: ${colors.grey};
  margin-top: 0.2rem;
`

const SuggestionLoading = styled.div`
  padding: 1.2rem 1.4rem;
  font-size: 1.3rem;
  color: ${colors.grey};
`